import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, MessageCircle, ArrowUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return '';
  }
}

export default function NotificationBell({ user }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setItems([]);
      setUnseen(0);
      return;
    }
    try {
      const r = await axios.get(`${API}/community/notifications`, { withCredentials: true });
      setItems(r.data?.items || []);
      setUnseen(r.data?.unseen_count || 0);
      setLoaded(true);
    } catch {
      /* silent for non-members */
    }
  }, [user]);

  useEffect(() => {
    reload();
    // Poll every 60s while logged in
    const interval = user ? setInterval(reload, 60_000) : null;
    return () => { if (interval) clearInterval(interval); };
  }, [reload, user]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [open]);

  const handleToggle = async (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && unseen > 0) {
      // Mark seen on open
      try {
        await axios.post(`${API}/community/notifications/seen`, {}, { withCredentials: true });
        setUnseen(0);
      } catch {
        /* silent */
      }
    }
  };

  if (!user) return null;

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-1.5 rounded-md hover:bg-muted transition"
        data-testid="notification-bell-btn"
        title="Notifications"
      >
        <Bell className="w-4 h-4 text-muted-foreground" />
        {unseen > 0 ? (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {unseen > 9 ? '9+' : unseen}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-popover shadow-2xl z-40 overflow-hidden"
          data-testid="notification-bell-dropdown"
        >
          <div className="px-4 py-2.5 border-b border-border bg-muted/40 flex items-center justify-between">
            <p className="text-sm font-bold">Notifications</p>
            <Link
              to="/community?tab=feed"
              className="text-[11px] text-violet-600 dark:text-violet-400 hover:underline"
              onClick={() => setOpen(false)}
            >
              Open feed →
            </Link>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loaded && items.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                Nothing new. You're all caught up.
              </p>
            ) : null}
            {items.map((n) => (
              <Link
                key={n.id}
                to={`/community?tab=feed`}
                className="block px-4 py-2.5 border-b border-border hover:bg-muted/30 transition"
                onClick={() => setOpen(false)}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 flex items-center justify-center flex-shrink-0">
                    {n.type === 'reply' ? <MessageCircle className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs">
                      <span className="font-bold">@{n.handle}</span>
                      {' '}{n.is_reply_to_comment ? 'replied to your comment' : 'commented on your post'}
                    </p>
                    {n.preview ? (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{n.preview}</p>
                    ) : null}
                    <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)} ago</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
